import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildingLicenseApi, enumsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { showError } from '../utils/showError';
import { extractErrorMessage } from '../utils/errorUtils';
import {
  PageHeader,
  LoadingSpinner,
  EmptyState,
  ErrorMessage,
  SuccessMessage,
  Button,
  Badge,
  Card,
  ConfirmationModal,
} from '../components/common';
import ModuleBadges from '../components/licenses/ModuleBadges';
import { ShieldCheck, Lock } from 'lucide-react';
import {
  getLicenseStatusName,
  getLicenseStatusColor,
  formatPlanLimits,
} from '../constants/licenses';

const License = () => {
  const { selectedBuildingId, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const buildingId = (isAdmin && searchParams.get('buildingId')) || selectedBuildingId;

  const [license, setLicense] = useState(null);
  const [plans, setPlans] = useState([]);
  const [moduleTypes, setModuleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Change plan confirmation
  const [changeTarget, setChangeTarget] = useState(null);
  const [changing, setChanging] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [licenseRes, plansRes, modulesRes] = await Promise.all([
        buildingLicenseApi.getForBuilding(buildingId).catch((err) => {
          if (err.response?.status === 404) return null; // no active license
          throw err;
        }),
        buildingLicenseApi.availablePlans(buildingId),
        enumsApi.getModuleTypes(),
      ]);
      setLicense(licenseRes ? licenseRes.data : null);
      setPlans(
        (plansRes.data || []).slice().sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      );
      setModuleTypes(modulesRes.data || []);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (buildingId) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  const handleChangePlan = async () => {
    setChanging(true);
    try {
      await buildingLicenseApi.changePlan({ buildingId, targetPlanId: changeTarget.licensePlanId });
      setChangeTarget(null);
      setSuccessMessage('Plan changed successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchData();
    } catch (err) {
      showError(err);
      setChangeTarget(null);
    } finally {
      setChanging(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (plan) => {
    if (!plan.monthlyPrice && !plan.annualPrice) return 'Free';
    const parts = [];
    if (plan.monthlyPrice) parts.push(`$${plan.monthlyPrice}/mo`);
    if (plan.annualPrice) parts.push(`$${plan.annualPrice}/yr`);
    return parts.join(' · ');
  };

  if (!buildingId || loading) {
    return <LoadingSpinner />;
  }

  const allowedModules = license?.effectiveAllowedModules || [];
  const excludedModules = license
    ? moduleTypes.filter((m) => !allowedModules.includes(m.value))
    : [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="License"
        subtitle="View your building's license and compare available plans"
        icon={ShieldCheck}
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
      {successMessage && <SuccessMessage message={successMessage} onDismiss={() => setSuccessMessage(null)} />}

      {license ? (
        <Card>
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{license.planName}</h3>
              <p className="text-sm text-gray-500 font-mono">{license.planCode}</p>
            </div>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLicenseStatusColor(license.status)}`}>
              {getLicenseStatusName(license.status)}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Start Date</p>
              <p className="font-medium text-gray-900">{formatDate(license.startDate)}</p>
            </div>
            <div>
              <p className="text-gray-500">Expiry Date</p>
              <p className="font-medium text-gray-900">
                {license.expiryDate ? formatDate(license.expiryDate) : 'Never expires'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Members</p>
              <p className="font-medium text-gray-900">
                {license.currentMemberCount} / {license.effectiveMaxUsers ?? 'Unlimited'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Units</p>
              <p className="font-medium text-gray-900">
                {license.currentUnitCount} / {license.effectiveMaxUnits ?? 'Unlimited'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Floors Limit</p>
              <p className="font-medium text-gray-900">{license.effectiveMaxFloors ?? 'Unlimited'}</p>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-2">Included Modules</p>
            <ModuleBadges modules={allowedModules} variant="badges" maxVisible={99} />
            {excludedModules.length > 0 && (
              <>
                <p className="text-sm text-gray-500 mt-4 mb-2">Not Included</p>
                <div className="flex flex-wrap gap-1">
                  {excludedModules.map((m) => (
                    <Badge key={m.value} variant="custom" className="bg-gray-100 text-gray-400 text-xs whitespace-nowrap">
                      <Lock size={12} className="mr-1" />
                      {m.displayName || m.name}
                    </Badge>
                  ))}
                </div>
              </>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState
            icon={ShieldCheck}
            title="No active license"
            description="This building has no active license (it may be missing, suspended, or cancelled). Contact support to activate a license."
          />
        </Card>
      )}

      {/* Compare Plans */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Compare Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card key={plan.licensePlanId} className={plan.isCurrent ? 'ring-2 ring-primary-500' : ''}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{plan.name}</h3>
                  <p className="text-sm text-gray-500 font-mono">{plan.planCode}</p>
                </div>
                {plan.isCurrent && (
                  <Badge variant="primary" size="small">Current plan</Badge>
                )}
              </div>
              {plan.description && <p className="mt-2 text-sm text-gray-600">{plan.description}</p>}
              <p className="mt-3 text-lg font-semibold text-gray-900">{formatPrice(plan)}</p>
              <p className="mt-1 text-sm text-gray-600">{formatPlanLimits(plan)}</p>
              <div className="mt-3">
                <ModuleBadges modules={plan.allowedModules} variant="list" />
              </div>
              {license && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <Button
                    variant={plan.isCurrent ? 'secondary' : 'primary'}
                    disabled={plan.isCurrent}
                    fullWidth
                    onClick={() => setChangeTarget(plan)}
                  >
                    {plan.isCurrent ? 'Current plan' : 'Change plan'}
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!changeTarget}
        onClose={() => setChangeTarget(null)}
        onConfirm={handleChangePlan}
        title="Change Plan"
        message={`Change plan from "${license?.planName}" to "${changeTarget?.name}"? Your current license will be cancelled and a new one created on the target plan.`}
        confirmText="Change Plan"
        confirmVariant="primary"
        loading={changing}
      />
    </div>
  );
};

export default License;
